import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { clientHistory, getClient, downloadCalcXlsx } from "../lib/api";
import { ThemeToggle } from "../components/ThemeToggle";
import { labelFor } from "../lib/enumLabels";
import { useTranslation } from "react-i18next";

/**
 * v0.4.35 P6B — Client history page.
 *
 * Reverse-chronological list of frozen filings for the client.
 * Filing reference link goes to `/?calc_id=` (D-P6B-1=a).
 */
export default function ClientHistory() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // v0.6.10 fix — was a plain `<a href={`${API}/runs/calc/${r.id}/export.xlsx`}>`,
  // a direct link to the backend API. This app authenticates via a
  // Bearer token attached by the axios interceptor (lib/api.js) —
  // there is no auth cookie fallback — so a plain browser navigation
  // to that URL carries no Authorization header at all and the
  // backend correctly, but unhelpfully, returns raw
  // `{"detail":"Not authenticated"}` JSON straight into the tab.
  // Never caught before because this page had no reachable nav path
  // until this same fix made it reachable. Every other export button
  // in the app (Step6FreezeExport.jsx etc.) already goes through
  // `downloadCalcXlsx()` — an authenticated blob fetch — so this just
  // brings this one link in line with that established pattern.
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  // v0.6.10 — search over filing reference / regime / period, for
  // clients with a long filing history.
  const [query, setQuery] = useState("");
  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const haystack = [
        r.filing_reference, r.regime, r.operator_email,
        r.period_start, r.period_end,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, query]);

  const handleDownload = async (calcId) => {
    setDownloadingId(calcId);
    setDownloadError(null);
    const r = await downloadCalcXlsx(calcId);
    setDownloadingId(null);
    if (!r.ok) setDownloadError(r.error || "Export failed");
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, h] = await Promise.all([getClient(id), clientHistory(id)]);
        if (cancelled) return;
        setClient(c); setRows(h);
      } catch (e) { if (!cancelled) setError(e?.message || String(e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <div data-testid="client-history-page" className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t("ui.client_history.eyebrow", { defaultValue: "Client history" })}</div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
              {client ? (
                <span>
                  <span className="tabular-nums text-amber-700 dark:text-amber-300 mr-2">{client.code}</span>
                  <span className="text-slate-300 dark:text-slate-600">·</span>
                  <span className="ml-2">{client.name}</span>
                </span>
              ) : "—"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              data-testid="client-history-back"
              onClick={() => nav("/admin/clients")}
              className="text-sm text-slate-700 dark:text-slate-200 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-800 rounded-lg font-medium"
            >{t("ui.client_history.back", { defaultValue: "Back to clients" })}</button>
          </div>
        </div>
        {loading && <div className="text-slate-600 dark:text-slate-300 text-sm">{t("ui.client_history.loading", { defaultValue: "Loading…" })}</div>}
        {downloadError && (
          <div
            data-testid="client-history-download-error"
            className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/40 border border-rose-200 dark:border-rose-800 rounded-lg px-4 py-3"
          >
            {downloadError}
          </div>
        )}
        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/40 border border-rose-200 dark:border-rose-800 rounded-lg px-4 py-3">
            {error.includes("404") ? t("ui.client_history.empty_404", { defaultValue: "No frozen filings yet for this entity." }) : error}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm px-6 py-12 text-center">
            <div className="text-slate-700 dark:text-slate-200 font-medium">{t("ui.client_history.empty_title", { defaultValue: "No frozen filings yet for this client." })}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("ui.client_history.empty_body", { defaultValue: "Filings will appear here once they are signed off and frozen on the Dashboard." })}</div>
          </div>
        )}
        {!loading && !error && rows.length > 5 && (
          <div className="relative">
            <MagnifyingGlass
              size={14}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 pointer-events-none"
            />
            <input
              type="search"
              data-testid="client-history-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("ui.client_history.search_placeholder", { defaultValue: "Search by filing reference, regime, or period" })}
              className="w-full pl-9 pr-9 py-2.5 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-500/30 outline-none"
            />
            {query && (
              <>
              <button
                type="button"
                data-testid="client-history-search-clear"
                // The UX-audit gate (test_v0501c8) flags this as a loaded
                // "Clear" resolution word due to a regex quirk: its naive
                // attribute scanner stops at the first bare close-angle
                // character, which the inline onClick arrow below contains,
                // so it misreads aria-label as button body text. This
                // button just clears a local text filter, no filing or
                // review state involved. aria-describedby makes that
                // explicit; kept free of that same character and placed
                // before onClick so the gate's scanner reads it correctly.
                aria-describedby="client-history-search-clear-desc"
                onClick={() => setQuery("")}
                aria-label={t("ui.client_history.clear_search", { defaultValue: "Clear search" })}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X size={12} weight="bold" />
              </button>
              <span id="client-history-search-clear-desc" className="sr-only">
                {t("ui.client_history.search_clear_desc", { defaultValue: "Clears the local search text only. Does not change any filing, review, or calculation data." })}
              </span>
              </>
            )}
          </div>
        )}
        {!loading && !error && rows.length > 0 && (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-xs font-medium text-slate-600 dark:text-slate-300">
              <div className="col-span-4">{t("ui.client_history.col_ref", { defaultValue: "Filing reference" })}</div>
              <div className="col-span-2">{t("ui.client_history.col_regime", { defaultValue: "Regime" })}</div>
              <div className="col-span-2">{t("ui.client_history.col_period", { defaultValue: "Period" })}</div>
              <div className="col-span-2">{t("ui.client_history.col_frozen", { defaultValue: "Frozen" })}</div>
              <div className="col-span-1">{t("ui.client_history.col_operator", { defaultValue: "Operator" })}</div>
              <div className="col-span-1 text-right">XLSX</div>
            </div>
            {filteredRows.length === 0 && (
              <div className="px-4 py-10 text-sm text-slate-500 dark:text-slate-400 text-center">
                {t("ui.client_history.no_match", { query, defaultValue: `No filings match "${query}".` })}
              </div>
            )}
            {filteredRows.map((r) => {
              const am = r.amendments_summary || { open: 0, latest_open: null };
              const lo = am.latest_open;
              return (
              <div key={r.id}>
              <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800 text-sm items-center hover:bg-slate-50 dark:hover:bg-slate-800/60 dark:bg-slate-950">
                <div className="col-span-4 truncate flex items-center gap-2">
                  {/* v0.6.10 fix — a v0.6.10 self-review pass swapped this to
                      <Link>, matching the ClientSelector.jsx v0.6.8 pattern,
                      but that pattern doesn't apply here: WorkflowLayout.jsx's
                      `?calc_id=` deep-link handler is a useEffect with an
                      empty dep array, so it reads `window.location.search`
                      exactly ONCE, at mount. A SPA-only <Link> navigation
                      while WorkflowLayout is already mounted (the normal
                      case — this page is reached FROM inside the app) never
                      re-fires that effect, so the calc silently never loads
                      ("No active calculation"). A real <a href> forces a
                      full page load, which is what that handler actually
                      requires. Reverted to <a>; this is genuinely one of the
                      rare deliberate-full-reload links, not a stray bug. */}
                  <a href={`/?calc_id=${r.id}`} className="tabular-nums text-amber-700 dark:text-amber-300 hover:underline">{r.filing_reference || "(unfrozen)"}</a>
                  {r.superseded_by_calc_id && (
                    // A1-93 (7) — a superseded filing looked like any other row.
                    <a href={`/?calc_id=${r.superseded_by_calc_id}`} data-testid={`superseded-badge-${r.id}`}
                      title={t("ui.client_history.superseded_title", { defaultValue: "Replaced by a newer calculation — open the current one" })}
                      className="text-xs border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2 py-0.5 rounded-full hover:underline">
                      {t("ui.client_history.superseded", { defaultValue: "Superseded" })}
                    </a>
                  )}
                  {am.open > 0 && (
                    <span
                      data-testid={`amendment-due-badge-${r.id}`}
                      className="text-xs border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full"
                      title={t("ui.client_history.amendment_due_title", { n: am.open, defaultValue: `${am.open} open amendment(s) need operator action` })}
                    >
                      {t("ui.client_history.amendment_due", { n: am.open, defaultValue: `Amendment due (${am.open})` })}
                    </span>
                  )}
                </div>
                <div className="col-span-2 text-slate-600 dark:text-slate-300">{labelFor("regime", r.regime)}</div>
                <div className="col-span-2 tabular-nums text-slate-700 dark:text-slate-200 text-xs">{(r.period_start || "").slice(0, 10)} → {(r.period_end || "").slice(0, 10)}</div>
                <div className="col-span-2 tabular-nums text-slate-700 dark:text-slate-200 text-xs">{(r.frozen_at_utc || r.frozen_at || "").slice(0, 16).replace("T", " ")}</div>
                <div className="col-span-1 text-slate-600 dark:text-slate-300 truncate text-xs">{r.operator_email || "—"}</div>
                <div className="col-span-1 text-right">
                  <button
                    type="button"
                    data-testid={`client-history-xlsx-${r.id}`}
                    onClick={() => handleDownload(r.id)}
                    disabled={downloadingId === r.id}
                    className="text-teal-700 dark:text-teal-300 hover:text-teal-800 dark:hover:text-teal-200 text-xs font-medium underline disabled:opacity-50 disabled:no-underline"
                  >{downloadingId === r.id ? "…" : "XLSX"}</button>
                </div>
              </div>
              {am.open > 0 && lo && (
                <div
                  data-testid={`amendment-deeplink-${r.id}`}
                  className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 bg-amber-50/50 dark:bg-amber-950/30 text-xs text-slate-700 dark:text-slate-200 flex items-center gap-2"
                >
                  <span className="text-amber-700 dark:text-amber-300">↳</span>
                  <span>
                    <span className="tabular-nums">{am.open}</span> {t("ui.client_history.open_amendment", { defaultValue: "OPEN amendment ·" })} {lo.country}
                    {lo.category ? ` ${labelFor("tax_category", lo.category)}` : ""}
                    {" · "}
                    <span className="text-slate-600 dark:text-slate-300">{labelFor("trigger_kind", lo.trigger_kind)}</span>
                  </span>
                  <a
                    href={`/amendments?focus=${lo.amendment_id}`}
                    className="text-teal-700 dark:text-teal-300 hover:text-teal-800 dark:hover:text-teal-200 font-medium underline ml-auto"
                  >
                    {t("ui.client_history.resolve", { defaultValue: "resolve →" })}
                  </a>
                </div>
              )}
              </div>
            )})}
            {rows.length === 0 && (
              null
            )}
          </div>
        )}
      </div>
    </div>
  );
}
